import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import utils from "../../../utils/utils";
import { normListId } from "../../common/dualListBox";
import {
  getActiveSiteLookup,
  getInternalActiveStaffLookup,
  siteToDropdownOptions,
  staffToDropdownOptions,
} from "../../../services/lookupService";
import { guidToUpperCase } from "../../../utils/guidUtils";

const pick = (obj, ...keys) => {
  if (obj == null) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const idToUpperCase = (v) => guidToUpperCase(v) ?? "";

const normalizeToArray = (raw) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") {
    const vals = Object.values(raw);
    if (vals.length && typeof vals[0] === "object") return vals;
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === "object") return Object.values(parsed);
    } catch {
      return [];
    }
  }
  return [];
};

const USER_ASSIGNMENT_KEYS = [
  "StudyUsers",
  "studyUsers",
  "UserList",
  "userList",
  "AssignedUsers",
  "assignedUsers",
  "AssignedStaffList",
  "assignedStaffList",
];

function unwrapActivityPayload(res) {
  if (!res || typeof res !== "object") return {};
  let inner = res.data ?? res.Data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    const nested = inner.data ?? inner.Data;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      const merged = { ...nested };
      for (const key of USER_ASSIGNMENT_KEYS) {
        if (!normalizeToArray(merged[key]).length && inner[key] != null) {
          merged[key] = inner[key];
        }
      }
      inner = merged;
    }
    return inner;
  }
  if (
    res.StudyInfo != null ||
    res.studyInfo != null ||
    res.StudyUsers != null ||
    res.studyUsers != null
  ) {
    return res;
  }
  return {};
}

function getFirstStudyInfo(payload) {
  if (!payload || typeof payload !== "object") return null;
  const raw = pick(payload, "StudyInfo", "studyInfo", "Study", "study");
  if (Array.isArray(raw) && raw.length) return raw[0];
  if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (
    pick(payload, "StudyNumber", "studyNumber") != null ||
    pick(payload, "StudyName", "studyName") != null
  ) {
    return payload;
  }
  return null;
}

function getStudyUsersList(payload) {
  if (!payload || typeof payload !== "object") return [];
  for (const key of USER_ASSIGNMENT_KEYS) {
    const rows = normalizeToArray(payload[key]);
    if (rows.length) return rows;
  }
  return [];
}

function isAssignedUserRow(row) {
  if (!row || typeof row !== "object") return false;
  const value = row.IsAssigned ?? row.isAssigned;
  if (value === undefined || value === null || value === "") return true;
  if (value === false || value === 0 || value === "0") return false;
  return true;
}

function pickUserRowId(row) {
  return normListId(
    pick(
      row,
      "UserId",
      "userId",
      "StaffId",
      "staffId",
      "StaffID",
      "StaffIDRef",
      "UserIdRef",
      "userIdRef",
    ),
  );
}

function userRowLabel(row, staffById) {
  const direct =
    pick(row, "StaffName", "staffName", "UserName", "userName", "FullName", "fullName") ||
    "";
  if (direct) return String(direct).trim();
  const id = pickUserRowId(row);
  const staff = id ? staffById.get(id) : null;
  if (staff) {
    return (
      staff.label ||
      staff.staff?.StaffName ||
      `${staff.staff?.FirstName || ""} ${staff.staff?.LastName || ""}`.trim()
    );
  }
  const email = pick(row, "EmailId", "emailId", "Email", "email");
  return email ? String(email).trim() : "";
}

function parseNameAndEmployeeNum(label, staffRow) {
  let name = String(label || "").trim();
  let employeeNum = staffRow?.EmployeeNum ? String(staffRow.EmployeeNum) : "";
  if (!employeeNum && name) {
    const match = name.match(/\((\d+)\)\s*$/);
    if (match) {
      employeeNum = match[1];
      name = name.replace(/\s*\(\d+\)\s*$/, "").trim();
    }
  }
  return { name: name || "Assigned user", employeeNum };
}

function buildUserFromRow(row, staffById) {
  const id = pickUserRowId(row);
  const staff = id ? staffById.get(id) : null;
  const label = userRowLabel(row, staffById);
  const { name, employeeNum } = parseNameAndEmployeeNum(label, staff?.staff);
  return {
    id: id || name,
    name,
    employeeNum,
    label: employeeNum ? `${name} (${employeeNum})` : name,
  };
}

function buildUserFromStaffId(staffId, staffById, fallbackName = "") {
  const staff = staffId ? staffById.get(staffId) : null;
  const label =
    staff?.label ||
    staff?.staff?.StaffName ||
    `${staff?.staff?.FirstName || ""} ${staff?.staff?.LastName || ""}`.trim() ||
    fallbackName;
  const { name, employeeNum } = parseNameAndEmployeeNum(label, staff?.staff);
  return {
    id: staffId || name,
    name,
    employeeNum,
    label: employeeNum ? `${name} (${employeeNum})` : name,
  };
}

function parseMappedSiteIds(raw) {
  return String(raw ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

function buildStaffMap(staffRaw = []) {
  const map = new Map();
  staffToDropdownOptions(staffRaw).forEach((opt) => {
    const id = normListId(opt.value);
    if (id) map.set(id, opt);
  });
  return map;
}

function collectAssignedUsers(payload, info, staffById, rawEnvelope) {
  const rows = [
    ...getStudyUsersList(payload),
    ...getStudyUsersList(rawEnvelope),
    ...(rawEnvelope?.data ? getStudyUsersList(rawEnvelope.data) : []),
    ...(rawEnvelope?.Data ? getStudyUsersList(rawEnvelope.Data) : []),
  ];
  const seen = new Set();
  const users = [];

  const pushRow = (row, respectAssignFlag) => {
    if (!row || typeof row !== "object") return;
    if (respectAssignFlag && !isAssignedUserRow(row)) return;
    const user = buildUserFromRow(row, staffById);
    const key = user.id || user.name.toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    users.push(user);
  };

  rows.forEach((row) => pushRow(row, true));
  if (!users.length) rows.forEach((row) => pushRow(row, false));

  const piId = normListId(
    pick(
      info,
      "PrincipalInvestigator",
      "principalInvestigator",
      "PI",
      "pi",
      "PIStaffId",
      "piStaffId",
    ),
  );
  if (piId && !seen.has(piId)) {
    const user = buildUserFromStaffId(
      piId,
      staffById,
      pick(info, "PIName", "pIName", "piName") || "Principal Investigator",
    );
    users.unshift({ ...user, isPi: true });
  }

  return users;
}

function buildFallbackSummary(studyFromNav, cohort) {
  const studyNumber =
    pick(studyFromNav, "StudyNumber", "studyNumber") ||
    pick(cohort?.study, "StudyNumber", "studyNumber") ||
    "";
  const studyName =
    pick(studyFromNav, "StudyName", "studyName") ||
    cohort?.studyName ||
    pick(cohort?.study, "StudyName", "studyName") ||
    "";
  const rawPi =
    pick(studyFromNav, "PIName", "pIName", "piName") ||
    pick(cohort?.study, "PIName", "pIName", "piName") ||
    "";
  const piParsed = parseNameAndEmployeeNum(rawPi, null);

  return {
    studyNumber: studyNumber ? String(studyNumber) : "",
    studyName: studyName ? String(studyName) : "",
    studyColor:
      pick(studyFromNav, "StudySchColor", "studySchColor", "StudyColor", "studyColor") ||
      cohort?.studyColor ||
      "#2563eb",
    studyPrefix:
      pick(studyFromNav, "Prefix", "prefix") ||
      pick(cohort?.study, "Prefix", "prefix") ||
      "STD",
    piName: piParsed.name,
    piEmployeeNum: piParsed.employeeNum,
    sites: [],
    assignedUsers: piParsed.name
      ? [
          {
            id: "pi-fallback",
            name: piParsed.name,
            employeeNum: piParsed.employeeNum,
            label: piParsed.employeeNum
              ? `${piParsed.name} (${piParsed.employeeNum})`
              : piParsed.name,
            isPi: true,
          },
        ]
      : [],
    source: "fallback",
  };
}

export async function fetchScheduleStudyInfoSummary({
  studyId,
  organizationId,
  staffId,
  studyFromNav = null,
  cohort = null,
}) {
  const fallback = buildFallbackSummary(studyFromNav, cohort);
  if (!studyId || !organizationId || !staffId) {
    return fallback;
  }

  try {
    const userCtx = { OrganizationId: organizationId, StaffId: staffId };
    const [studyRes, siteRows, staffRaw] = await Promise.all([
      api.post(API_ENDPOINTS.STUDY.STUDY_ACTIVITY, {
        OrganizationId: idToUpperCase(organizationId),
        CreatedBy: idToUpperCase(staffId),
        StaffIdRef: idToUpperCase(staffId),
        StudyId: idToUpperCase(studyId),
        Action: "GetStudyInfo",
      }),
      getActiveSiteLookup(userCtx),
      getInternalActiveStaffLookup(userCtx),
    ]);

    if (!utils.isApiSuccess(studyRes)) {
      return fallback;
    }

    const payload = unwrapActivityPayload(studyRes);
    const info = getFirstStudyInfo(payload);
    if (!info) return fallback;

    const staffById = buildStaffMap(staffRaw);
    const siteOptions = siteToDropdownOptions(siteRows);

    const studyNumber = String(pick(info, "StudyNumber", "studyNumber") || fallback.studyNumber || "");
    const studyName = String(pick(info, "StudyName", "studyName") || fallback.studyName || "");
    const piStaffId = normListId(
      pick(
        info,
        "PrincipalInvestigator",
        "principalInvestigator",
        "PI",
        "pi",
        "PIStaffId",
        "piStaffId",
      ),
    );
    const piStaff = piStaffId ? staffById.get(piStaffId) : null;
    const piParsed = parseNameAndEmployeeNum(
      (piStaff &&
        (piStaff.label ||
          piStaff.staff?.StaffName ||
          `${piStaff.staff?.FirstName || ""} ${piStaff.staff?.LastName || ""}`.trim())) ||
        pick(info, "PIName", "pIName", "piName") ||
        fallback.piName ||
        "",
      piStaff?.staff,
    );

    let mappedSiteIds = parseMappedSiteIds(pick(info, "MappedSiteIds", "mappedSiteIds"));
    if (!mappedSiteIds.length) {
      const singleSite = pick(
        info,
        "SiteID",
        "SiteId",
        "siteId",
        "StudySiteID",
        "studySiteID",
      );
      if (singleSite != null && singleSite !== "") {
        mappedSiteIds = [String(singleSite)];
      }
    }

    const sites = mappedSiteIds.map((siteId) => {
      const match = siteOptions.find(
        (opt) => String(opt.value).toLowerCase() === String(siteId).toLowerCase(),
      );
      return {
        id: siteId,
        name: match?.label || `Site ${siteId}`,
      };
    });

    const assignedUsers = collectAssignedUsers(payload, info, staffById, studyRes).filter(
      (user) => !user.isPi,
    );

    return {
      studyNumber,
      studyName,
      studyColor:
        pick(info, "StudySchColor", "studySchColor", "StudyColor", "studyColor") ||
        cohort?.studyColor ||
        fallback.studyColor,
      studyPrefix:
        pick(info, "Prefix", "prefix") ||
        pick(studyFromNav, "Prefix", "prefix") ||
        fallback.studyPrefix,
      piName: piParsed.name,
      piEmployeeNum: piParsed.employeeNum,
      sites,
      assignedUsers,
      source: "api",
    };
  } catch {
    return fallback;
  }
}
